/**
 * Holds a gift code across a login round trip.
 *
 * A recipient arriving from a gift email or a printed QR code lands on
 * `/redeem?code=…`, but signing in with Google or a magic link leaves the site
 * and returns to `/auth/callback`, losing the query string. Stashing the code
 * lets us send them straight back to `/redeem` and activate the gift without
 * ever asking them to type it in.
 */

const KEY = 'worlds_pending_gift_code';
/** Long enough for a slow email round trip, short enough not to linger. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

interface Stored {
    code: string;
    at: number;
}

export function rememberPendingGiftCode(code: string): void {
    const value = code.trim().toUpperCase();
    if (!value) return;
    try {
        localStorage.setItem(KEY, JSON.stringify({ code: value, at: Date.now() } satisfies Stored));
    } catch {
        // Private browsing / storage disabled — the code stays in the URL.
    }
}

export function readPendingGiftCode(): string | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<Stored>;
        if (typeof parsed?.code !== 'string' || typeof parsed?.at !== 'number') {
            localStorage.removeItem(KEY);
            return null;
        }
        if (Date.now() - parsed.at > MAX_AGE_MS) {
            localStorage.removeItem(KEY);
            return null;
        }
        return parsed.code;
    } catch {
        return null;
    }
}

export function clearPendingGiftCode(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        // Nothing to clean up.
    }
}
